import { useKernelApi } from 'infrastructure/api';
import { useBaseCrudService } from 'framework/crud';
import { LoadingParams } from 'framework/list';
import { FormFieldsData } from 'framework/form';
import { RESOURCE_URL as EMPLOYEES_RESOURCE_URL } from './constants';
import { useEmployeeId } from './route/hooks/useEmployeeId';
import educationValidation from './validation/educationValidation';
import createEducationValidation from './validation/createEducationValidation';

const EDUCATIONS_RESOURCE_URL = '/educations';

export const EDUCATION_OPTIONS = {
  HIGHER: 'Higher Education',
  SECONDARY_SPECIAL: 'Secondary Special Education',
  COURSE: 'Course',
  TRAINING: 'Training',
};

export const DOCUMENT_TYPES = {
  DIPLOMA: 'Diploma',
  CERTIFICATE: 'Certificate',
};

const useEducationService = () => {
  const { sendGetRequest } = useKernelApi();
  const employeeId = useEmployeeId();
  const parentResourceUrl = `${EMPLOYEES_RESOURCE_URL}/${employeeId}`;
  const getValidationSchema = () => Promise.resolve(educationValidation);
  const getCreateValidationSchema = () => Promise.resolve(createEducationValidation);

  const mapFromApi = (education: { document?: any }) => {
    const haveDocument = !!education.document;

    return {
      haveDocument,
      ...education,
    };
  };

  const mapEducation = (education: any, isCreateForm?: boolean) => {
    const result = { ...education };

    if (!education.haveDocument) {
      if (isCreateForm) {
        delete result.document;
      } else {
        result.document = {};
      }
    }

    delete result.haveDocument;

    return result;
  };

  const baseCrudRequests = useBaseCrudService({
    singleResourceUrl: `${EDUCATIONS_RESOURCE_URL}/:id`,
    listResourceUrl: `${parentResourceUrl}${EDUCATIONS_RESOURCE_URL}`,
    useService: useKernelApi,
  });

  const getSearchParams = (request: LoadingParams) => {
    const params = [];
    params.push(`pageSize=${encodeURIComponent(request.pageSize)}`);
    params.push(`pageNumber=${encodeURIComponent(request.pageNumber)}`);

    if (Object.keys(request?.sort || {}).length > 0) {
      const [sortKey, sortValue] = Object.entries(request?.sort || {})[0];
      params.push(`${encodeURIComponent(`sort[${sortKey}]`)}=${encodeURIComponent(sortValue)}`);
    }

    return params.join('&');
  };

  const search = async (request: any) => {
    const url = `${parentResourceUrl}${EDUCATIONS_RESOURCE_URL}?${getSearchParams(request)}`;
    const response = await sendGetRequest(url);
    const result = await response.json();
    return result;
  };

  const { getById, update: baseUpdate, create: baseCreate, deleteById: baseDelete } = baseCrudRequests;

  const get = async (id: StringOrNumber) => {
    const response = await getById(id);

    return mapFromApi(response);
  };

  const update = async (id: StringOrNumber, education: any) => {
    return baseUpdate(id, mapEducation(education));
  };

  const deleteEducationById = async (id: StringOrNumber) => {
    return baseDelete(id);
  };

  const create = async (data: FormFieldsData) => {
    const educations = data.request || [];
    const request = educations.map((item: any) => mapEducation(item, true));

    return baseCreate({
      request,
    });
  };

  return {
    ...baseCrudRequests,
    getValidationSchema,
    getCreateValidationSchema,
    search,
    get,
    update,
    create,
    deleteEducationById,
    mapFromApi,
    mapEducation,
  };
};

export default useEducationService;
